import React, { useEffect, useRef } from 'react';
import './Modal.css';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  /**
   * Modal style:
   * - 'bottom-sheet': Slides up from bottom on mobile, centered modal on desktop
   * - 'centered': Always centered modal
   * - 'side-panel': Side panel on desktop, bottom sheet on mobile
   */
  variant?: 'bottom-sheet' | 'centered' | 'side-panel';
  /**
   * Whether clicking the overlay closes the modal
   */
  closeOnOverlayClick?: boolean;
  /**
   * Whether to show the close button
   */
  showCloseButton?: boolean;
  /**
   * Custom class name for the modal content
   */
  className?: string;
  /**
   * Max height for the modal (useful for bottom sheets)
   */
  maxHeight?: string;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  variant = 'bottom-sheet',
  closeOnOverlayClick = true,
  showCloseButton = true,
  className = '',
  maxHeight = '85vh',
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle overlay click
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle swipe down to close (only on drag handle, not scrollable content)
  const touchStartY = useRef<number>(0);
  const swipeTarget = useRef<boolean>(false);
  const handleDragTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
    swipeTarget.current = true;
  };

  const handleDragTouchEnd = (e: React.TouchEvent) => {
    if (!swipeTarget.current) return;
    swipeTarget.current = false;
    const touchEndY = e.changedTouches[0].clientY;
    const diff = touchEndY - touchStartY.current;
    // If swiped down more than 80px on the drag handle, close the modal
    if (diff > 80 && variant === 'bottom-sheet') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={`modal-overlay ${isOpen ? 'modal-open' : ''}`}
      onClick={handleOverlayClick}
    >
      <div
        ref={modalRef}
        className={`modal-content modal-${variant} ${className}`}
        style={{ maxHeight }}
      >
        {/* Drag handle for bottom sheet on mobile — swipe here to close */}
        {variant === 'bottom-sheet' && (
          <div
            className="modal-drag-handle"
            onTouchStart={handleDragTouchStart}
            onTouchEnd={handleDragTouchEnd}
          >
            <div className="drag-bar" />
          </div>
        )}

        {/* Header */}
        {(title || showCloseButton) && (
          <div className="modal-header">
            {title && <h2 className="modal-title">{title}</h2>}
            {showCloseButton && (
              <button className="modal-close-btn" onClick={onClose} aria-label="Close">
                ✕
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className="modal-body">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal;
